// components/InvoiceProductPickerDrawer.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Box,
  VStack,
  HStack,
  Image,
  Text,
  InputGroup,
  InputLeftElement,
  Input,
  IconButton,
  Badge,
  Button,
  Spinner,
  useToast,
  Tooltip,
  SimpleGrid,
  Kbd,
} from "@chakra-ui/react";
import { Search2Icon, RepeatIcon } from "@chakra-ui/icons";
import { useCognito } from "../utils/useCognito";
import { getProductListClean } from "../utils/commonApiCalls";

const HOVER_SCALE = { transform: "scale(1)" };

const resolveUnitPrice = (product) => {
  const hist = product?.historicalPrices || {};
  const dates = Object.keys(hist);
  if (dates.length) {
    const sorted = [...dates].sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime()
    );
    const latest = hist[sorted[0]];
    const val = Number(latest);
    if (!Number.isNaN(val) && Number.isFinite(val)) return val;
  }
  const base = Number(product?.price || 0);
  return Number.isNaN(base) ? 0 : base;
};

const getSizeList = (product) => {
  if (Array.isArray(product?.sizes) && product.sizes.length > 0) {
    return product.sizes;
  }
  const pnKeys = Object.keys(product?.partNumbers || {});
  if (pnKeys.length > 0) return pnKeys;
  return ["Standard"];
};

export default function InvoiceProductPickerDrawer({
  isOpen,
  onClose,
  onPick, // ({ product, sizeKey, label, unitPrice }) => void
  selectedProductId,
  selectedSizeKey,
}) {
  const toast = useToast();
  const { getIdToken } = useCognito();

  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const token = getIdToken();
      const list = (await getProductListClean(token, true)) || [];
      const items = Array.isArray(list)
        ? list
        : Array.isArray(list?.items)
        ? list.items
        : [];
      setProducts(items);
    } catch (err) {
      console.error("[InvoiceProductPickerDrawer] getProductListClean failed:", err);
      toast({
        title: "Failed to load products",
        description:
          err?.message === "Failed to fetch"
            ? "Network/CORS issue on product list endpoint."
            : err?.message || String(err),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [getIdToken, toast]);

  useEffect(() => {
    if (!isOpen) return;
    // Load fresh when opened so pricing stays current
    loadProducts();
  }, [isOpen, loadProducts]);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => {
      const hay = `${p.name || ""} ${p.description || ""} ${
        p.category || ""
      } ${p.id || ""} ${p.qCode || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [products, query]);

  const handleSizeClick = (product, sizeKey) => {
    if (!product) return;
    const unitPrice = resolveUnitPrice(product);
    const label = sizeKey
      ? `${product.name || product.id || product.qCode || "Product"} — ${sizeKey}`
      : product.name || product.id || product.qCode || "Product";

    onPick?.({
      product,
      sizeKey,
      label,
      unitPrice,
    });
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          Select Product for Invoice
        </DrawerHeader>

        <DrawerBody>
          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between" align="center">
              <InputGroup size="sm" maxW="320px">
                <InputLeftElement pointerEvents="none">
                  <Search2Icon />
                </InputLeftElement>
                <Input
                  placeholder="Search name, category, qCode…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </InputGroup>
              <Tooltip label="Reload products">
                <IconButton
                  aria-label="Reload products"
                  icon={<RepeatIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={loadProducts}
                  _hover={HOVER_SCALE}
                />
              </Tooltip>
            </HStack>

            {loading ? (
              <Box py={8} textAlign="center">
                <Spinner />
                <Text mt={2} fontSize="sm" color="gray.500">
                  Loading products…
                </Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={3}>
                {filtered.length === 0 && (
                  <Box
                    py={6}
                    textAlign="center"
                    borderWidth="1px"
                    borderRadius="lg"
                    borderStyle="dashed"
                    borderColor="gray.300"
                  >
                    <Text fontSize="sm" color="gray.500">
                      No products match your search.
                    </Text>
                  </Box>
                )}

                {filtered.map((p) => {
                  const id = p.id || p.qCode;
                  const sizes = getSizeList(p);
                  const unitPrice = resolveUnitPrice(p);
                  const isSelectedProduct =
                    selectedProductId && selectedProductId === id;

                  return (
                    <Box
                      key={id}
                      borderWidth="1px"
                      borderRadius="xl"
                      p={3}
                      _hover={{
                        borderColor: "teal.400",
                        shadow: "sm",
                        ...HOVER_SCALE,
                      }}
                      transition="all 0.12s ease-out"
                    >
                      <HStack align="flex-start" spacing={3}>
                        <Image
                          boxSize="56px"
                          objectFit="cover"
                          src={
                            p.imageUrl
                              ? `https://www.curagenesis.com/assets/img/graft/${p.imageUrl}`
                              : "https://via.placeholder.com/56x56.png?text=—"
                          }
                          alt={p.name}
                          borderRadius="md"
                          borderWidth="1px"
                        />
                        <Box flex="1">
                          <HStack justify="space-between" align="flex-start">
                            <Box>
                              <Text fontWeight="semibold" noOfLines={1}>
                                {p.name || "Untitled Product"}
                              </Text>
                              <HStack spacing={2} mt={1}>
                                {p.qCode && (
                                  <Text fontSize="xs" color="gray.500">
                                    QCode: <Kbd>{p.qCode}</Kbd>
                                  </Text>
                                )}
                                {p.category && (
                                  <Badge colorScheme="purple" variant="subtle">
                                    {p.category}
                                  </Badge>
                                )}
                              </HStack>
                              <Text fontSize="xs" color="gray.400" mt={1}>
                                {`Base Price: $${Number(unitPrice || 0).toFixed(
                                  2
                                )}`}
                              </Text>
                            </Box>
                          </HStack>

                          <Box mt={3}>
                            <Text fontSize="xs" mb={1} color="gray.500">
                              Sizes
                            </Text>
                            <SimpleGrid
                              columns={{ base: 3, md: 4 }}
                              spacing={2}
                            >
                              {sizes.map((sizeKey) => {
                                const isActive =
                                  isSelectedProduct &&
                                  selectedSizeKey === sizeKey;
                                return (
                                  <Button
                                    key={sizeKey}
                                    size="xs"
                                    variant={isActive ? "solid" : "outline"}
                                    colorScheme="blue"
                                    justifyContent="center"
                                    _hover={HOVER_SCALE}
                                    onClick={() =>
                                      handleSizeClick(p, sizeKey)
                                    }
                                  >
                                    <Text fontSize="xs" noOfLines={1}>
                                      {sizeKey}
                                    </Text>
                                  </Button>
                                );
                              })}
                            </SimpleGrid>
                          </Box>
                        </Box>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </VStack>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px">
          <Button variant="ghost" mr={3} onClick={onClose} _hover={HOVER_SCALE}>
            Close
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
